import fetchUserInfo from 'hub-http/userInfo';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFeatureTableSessionCache } from '../FeatureTableSessionCacheProvider';
import { fetchCurrency } from './fetchCurrency';
import { fetchFeatureCategories } from './fetchFeatures';
import { fetchOwnedProductWithinSkuCategory, findOwnedHubTierWithinSkuCategory, resetCachedProductInventory } from './fetchOwnedProductWithinSkuCategory';
import { fetchPurchaseOptions } from './fetchPurchaseOptions';
import { fetchProducts } from './fetchProducts';
import { fetchPurchaseMotions } from './fetchPurchaseMotions';
import { fetchCrmSuiteFeatures } from './fetchCrmSuiteFeatures';

// Within a browser session, we have no control of our consumers lifecycle maintenance.
// MODULE_FETCH_INITIALIZED_CACHE allows us to track which fetches have been initialized
// in case an upstream change causes the effect to re-run prior to the initial
// promise resolving
import { jsx as _jsx } from "react/jsx-runtime";
let MODULE_FETCH_INITIALIZED_CACHE = {
  featureCategories: {},
  purchaseMotions: {},
  products: {},
  purchaseOptions: {},
  ownedProductsWithinSkuCategory: {}
};

// For use cases like the pricing page where each page renders its own table,
// the component may be mounted and unmounted multiple times, causing the loss
// of internal state tracking of the data cache. Module caching allows us to store
// the value at the module level to prevent re-fetching previously requested data
let MODULE_BUYING_MOTION_PROPS_CACHE = {
  sessionCache: {}
};
let MODULE_CURRENCY_CACHE = undefined;
let MODULE_FEATURE_CATEGORIES_CACHE = {
  sessionCache: {}
};
let MODULE_IS_PRE_SEATS_PORTAL_CACHE = undefined;
let MODULE_OWNED_PRODUCTS_CACHE = {
  allOwnedProducts: undefined,
  sessionCache: {}
};
let MODULE_PRICES_CACHE = {
  sessionCache: {}
};
let MODULE_PRODUCTS_CACHE = {
  sessionCache: {}
};
let MODULE_PURCHASE_MOTIONS_CACHE = {
  sessionCache: {}
};
export const FeatureTableDataCacheContext = /*#__PURE__*/createContext({
  cachedBuyingMotionProps: MODULE_BUYING_MOTION_PROPS_CACHE,
  cachedCurrency: MODULE_CURRENCY_CACHE,
  cachedFeatureCategories: MODULE_FEATURE_CATEGORIES_CACHE,
  cachedOwnedProductsWithinSkuCategory: MODULE_OWNED_PRODUCTS_CACHE,
  cachedPrices: MODULE_PRICES_CACHE,
  cachedProducts: MODULE_PRODUCTS_CACHE,
  cachedPurchaseMotions: MODULE_PURCHASE_MOTIONS_CACHE,
  isPreSeatsPortal: true
});
export const FeatureTableDataCacheProvider = ({
  children
}) => {
  const {
    productApiNames,
    purchaseMotionLocation,
    sessionCacheKey,
    skuCategoryName
  } = useFeatureTableSessionCache();
  const [isPreSeatsPortal, setIsPreSeatsPortal] = useState(false);
  const [cachedBuyingMotionProps, setCachedBuyingMotionProps] = useState(MODULE_BUYING_MOTION_PROPS_CACHE);
  const [cachedCurrency, setCachedCurrency] = useState(MODULE_CURRENCY_CACHE);
  const [cachedFeatureCategories, setCachedFeatureCategories] = useState(MODULE_FEATURE_CATEGORIES_CACHE);
  const [cachedOwnedProductsWithinSkuCategory, setCachedOwnedProductsWithinSkuCategory] = useState(MODULE_OWNED_PRODUCTS_CACHE);
  const [cachedPrices, setCachedPrices] = useState(MODULE_PRICES_CACHE);
  const [cachedProducts, setCachedProducts] = useState(MODULE_PRODUCTS_CACHE);
  const [cachedPurchaseMotions, setCachedPurchaseMotions] = useState(MODULE_PURCHASE_MOTIONS_CACHE);
  const [fetchInitializedCache, setFetchInitializedCache] = useState(MODULE_FETCH_INITIALIZED_CACHE);
  const isValidProductComparison = useMemo(() => {
    var _cachedProducts$sessi, _cachedProducts$sessi2;
    return ((_cachedProducts$sessi = cachedProducts.sessionCache) === null || _cachedProducts$sessi === void 0 ? void 0 : _cachedProducts$sessi[sessionCacheKey]) && ((_cachedProducts$sessi2 = cachedProducts.sessionCache) === null || _cachedProducts$sessi2 === void 0 ? void 0 : _cachedProducts$sessi2[sessionCacheKey].length) > 0;
  }, [cachedProducts.sessionCache, sessionCacheKey]);
  const isComponentMounted = useRef(false);
  useEffect(() => {
    isComponentMounted.current = true;
    if (MODULE_IS_PRE_SEATS_PORTAL_CACHE === undefined) {
      void fetchUserInfo().then(userInfoResponse => {
        if (isComponentMounted.current) {
          setIsPreSeatsPortal(!userInfoResponse.user.scopes.includes('seats-access'));
        }
      });
    } else {
      setIsPreSeatsPortal(MODULE_IS_PRE_SEATS_PORTAL_CACHE);
    }
    if (MODULE_CURRENCY_CACHE === undefined) {
      void fetchCurrency().then(currencyCode => {
        if (isComponentMounted.current) {
          setCachedCurrency(currencyCode);
        }
      });
    } else {
      setCachedCurrency(MODULE_CURRENCY_CACHE);
    }
    return () => {
      isComponentMounted.current = false;
    };
  }, []);
  const isProductsRequestInitialized = useMemo(() => {
    var _fetchInitializedCach;
    return ((_fetchInitializedCach = fetchInitializedCache.products[sessionCacheKey]) === null || _fetchInitializedCach === void 0 ? void 0 : _fetchInitializedCach.isInitialized) === true;
  }, [fetchInitializedCache.products, sessionCacheKey]);
  useEffect(() => {
    // Downstream consumers may need to derive which products to display
    // this prevents the fetch from firing until the productApiNames are known
    if (productApiNames.length > 0) {
      if (!isProductsRequestInitialized) {
        if (MODULE_PRODUCTS_CACHE.sessionCache[sessionCacheKey] === undefined) {
          void fetchProducts(productApiNames, skuCategoryName).then(fetchedProducts => {
            MODULE_PRODUCTS_CACHE.sessionCache[sessionCacheKey] = fetchedProducts;
            if (isComponentMounted.current) {
              setCachedProducts(prevState => ({
                sessionCache: Object.assign({}, prevState.sessionCache, {
                  [sessionCacheKey]: fetchedProducts
                })
              }));
            }
          });
          MODULE_FETCH_INITIALIZED_CACHE.products[sessionCacheKey] = {
            isInitialized: true
          };
          setFetchInitializedCache(prevState => Object.assign({}, prevState, {
            products: Object.assign({}, prevState.products, {
              [sessionCacheKey]: {
                isInitialized: true
              }
            })
          }));
        } else {
          setCachedProducts(prevState => ({
            sessionCache: Object.assign({}, prevState.sessionCache, {
              [sessionCacheKey]: MODULE_PRODUCTS_CACHE.sessionCache[sessionCacheKey]
            })
          }));
        }
        if (MODULE_OWNED_PRODUCTS_CACHE.allOwnedProducts === undefined) {
          void fetchOwnedProductWithinSkuCategory(skuCategoryName).then(({
            allOwnedProducts,
            ownedProductWithinSkuCategory
          }) => {
            MODULE_OWNED_PRODUCTS_CACHE.allOwnedProducts = allOwnedProducts;
            MODULE_OWNED_PRODUCTS_CACHE.sessionCache[sessionCacheKey] = ownedProductWithinSkuCategory;
            if (isComponentMounted.current) {
              setCachedOwnedProductsWithinSkuCategory(prevState => Object.assign({}, prevState, {
                allOwnedProducts,
                sessionCache: Object.assign({}, prevState.sessionCache, {
                  [sessionCacheKey]: ownedProductWithinSkuCategory
                })
              }));
            }
          });
          MODULE_FETCH_INITIALIZED_CACHE.ownedProductsWithinSkuCategory[sessionCacheKey] = {
            isInitialized: true
          };
          setFetchInitializedCache(prevState => Object.assign({}, prevState, {
            ownedProductsWithinSkuCategory: Object.assign({}, prevState.ownedProductsWithinSkuCategory, {
              [sessionCacheKey]: {
                isInitialized: true
              }
            })
          }));
        } else {
          setCachedOwnedProductsWithinSkuCategory(prevState => Object.assign({}, prevState, {
            allOwnedProducts: MODULE_OWNED_PRODUCTS_CACHE.allOwnedProducts,
            sessionCache: Object.assign({}, prevState.sessionCache, {
              [sessionCacheKey]: findOwnedHubTierWithinSkuCategory(skuCategoryName, MODULE_OWNED_PRODUCTS_CACHE.allOwnedProducts || [])
            })
          }));
        }
      }
    }
  }, [isProductsRequestInitialized, productApiNames, sessionCacheKey, skuCategoryName]);
  const isFeatureCategoriesRequestInitialized = useMemo(() => {
    var _fetchInitializedCach2;
    return ((_fetchInitializedCach2 = fetchInitializedCache.featureCategories[sessionCacheKey]) === null || _fetchInitializedCach2 === void 0 ? void 0 : _fetchInitializedCach2.isInitialized) === true;
  }, [fetchInitializedCache.featureCategories, sessionCacheKey]);
  useEffect(() => {
    if (!isFeatureCategoriesRequestInitialized) {
      if (isValidProductComparison) {
        if (MODULE_FEATURE_CATEGORIES_CACHE.sessionCache[sessionCacheKey] === undefined) {
          const featureCategoryPromise = skuCategoryName === 'CRM_SUITE' ? fetchCrmSuiteFeatures(productApiNames) : fetchFeatureCategories({
            productApiNames
          });
          void featureCategoryPromise.then(featureCategories => {
            MODULE_FEATURE_CATEGORIES_CACHE.sessionCache[sessionCacheKey] = featureCategories;
            if (isComponentMounted.current) {
              setCachedFeatureCategories(prevState => ({
                sessionCache: Object.assign({}, prevState.sessionCache, {
                  [sessionCacheKey]: featureCategories
                })
              }));
            }
          });
          MODULE_FETCH_INITIALIZED_CACHE.featureCategories[sessionCacheKey] = {
            isInitialized: true
          };
          setFetchInitializedCache(prevState => Object.assign({}, prevState, {
            featureCategories: Object.assign({}, prevState.featureCategories, {
              [sessionCacheKey]: {
                isInitialized: true
              }
            })
          }));
        } else {
          setCachedFeatureCategories(prevState => ({
            sessionCache: Object.assign({}, prevState.sessionCache, {
              [sessionCacheKey]: MODULE_FEATURE_CATEGORIES_CACHE.sessionCache[sessionCacheKey]
            })
          }));
        }
      }
    }
  }, [isFeatureCategoriesRequestInitialized, isValidProductComparison, sessionCacheKey, productApiNames, skuCategoryName]);
  const isPurchaseOptionsRequestInitialized = useMemo(() => {
    var _fetchInitializedCach3;
    return ((_fetchInitializedCach3 = fetchInitializedCache.purchaseOptions[sessionCacheKey]) === null || _fetchInitializedCach3 === void 0 ? void 0 : _fetchInitializedCach3.isInitialized) === true;
  }, [fetchInitializedCache.purchaseOptions, sessionCacheKey]);
  useEffect(() => {
    if (!isPurchaseOptionsRequestInitialized) {
      var _cachedProducts$sessi3;
      const requestedProducts = (_cachedProducts$sessi3 = cachedProducts.sessionCache) === null || _cachedProducts$sessi3 === void 0 ? void 0 : _cachedProducts$sessi3[sessionCacheKey];
      if (requestedProducts !== null && requestedProducts !== void 0 && requestedProducts.length && isValidProductComparison && cachedCurrency) {
        if (MODULE_PRICES_CACHE.sessionCache[sessionCacheKey] === undefined || MODULE_BUYING_MOTION_PROPS_CACHE.sessionCache[sessionCacheKey] === undefined) {
          void fetchPurchaseOptions({
            productApiNames,
            currencyCode: cachedCurrency
          }).then(({
            purchaseOptionsAndCurrencyByProductApiName,
            buyingMotionPropsByProductApiName
          }) => {
            MODULE_PRICES_CACHE.sessionCache[sessionCacheKey] = purchaseOptionsAndCurrencyByProductApiName;
            MODULE_BUYING_MOTION_PROPS_CACHE.sessionCache[sessionCacheKey] = buyingMotionPropsByProductApiName;
            if (isComponentMounted.current) {
              setCachedPrices(prevState => ({
                sessionCache: Object.assign({}, prevState.sessionCache, {
                  [sessionCacheKey]: purchaseOptionsAndCurrencyByProductApiName
                })
              }));
              setCachedBuyingMotionProps(prevState => ({
                sessionCache: Object.assign({}, prevState.sessionCache, {
                  [sessionCacheKey]: buyingMotionPropsByProductApiName
                })
              }));
            }
          });
          MODULE_FETCH_INITIALIZED_CACHE.purchaseOptions[sessionCacheKey] = {
            isInitialized: true
          };
          setFetchInitializedCache(prevState => Object.assign({}, prevState, {
            purchaseOptions: Object.assign({}, prevState.purchaseOptions, {
              [sessionCacheKey]: {
                isInitialized: true
              }
            })
          }));
        } else {
          setCachedPrices(prevState => ({
            sessionCache: Object.assign({}, prevState.sessionCache, {
              [sessionCacheKey]: MODULE_PRICES_CACHE.sessionCache[sessionCacheKey]
            })
          }));
          setCachedBuyingMotionProps(prevState => ({
            sessionCache: Object.assign({}, prevState.sessionCache, {
              [sessionCacheKey]: MODULE_BUYING_MOTION_PROPS_CACHE.sessionCache[sessionCacheKey]
            })
          }));
        }
      }
    }
  }, [isPurchaseOptionsRequestInitialized, isValidProductComparison, cachedProducts.sessionCache, cachedCurrency, productApiNames, sessionCacheKey]);
  const isPurchaseMotionsInitialized = useMemo(() => {
    var _fetchInitializedCach4;
    return ((_fetchInitializedCach4 = fetchInitializedCache.purchaseMotions[sessionCacheKey]) === null || _fetchInitializedCach4 === void 0 ? void 0 : _fetchInitializedCach4.isInitialized) === true;
  }, [fetchInitializedCache.purchaseMotions, sessionCacheKey]);
  useEffect(() => {
    if (!isPurchaseMotionsInitialized) {
      if (isValidProductComparison && !cachedPurchaseMotions.sessionCache[sessionCacheKey]) {
        if (MODULE_PURCHASE_MOTIONS_CACHE.sessionCache[sessionCacheKey] === undefined) {
          void fetchPurchaseMotions({
            productApiNames,
            purchaseMotionLocation
          }).then(purchaseMotionByProductApiName => {
            MODULE_PURCHASE_MOTIONS_CACHE.sessionCache[sessionCacheKey] = purchaseMotionByProductApiName;
            if (isComponentMounted.current) {
              setCachedPurchaseMotions(prevState => ({
                sessionCache: Object.assign({}, prevState.sessionCache, {
                  [sessionCacheKey]: purchaseMotionByProductApiName
                })
              }));
            }
          });
          MODULE_FETCH_INITIALIZED_CACHE.purchaseMotions[sessionCacheKey] = {
            isInitialized: true
          };
          setFetchInitializedCache(prevState => Object.assign({}, prevState, {
            purchaseMotions: Object.assign({}, prevState.purchaseMotions, {
              [sessionCacheKey]: {
                isInitialized: true
              }
            })
          }));
        } else {
          setCachedPurchaseMotions(prevState => ({
            sessionCache: Object.assign({}, prevState.sessionCache, {
              [sessionCacheKey]: MODULE_PURCHASE_MOTIONS_CACHE.sessionCache[sessionCacheKey]
            })
          }));
        }
      }
    }
  }, [isPurchaseMotionsInitialized, isValidProductComparison, productApiNames, purchaseMotionLocation, sessionCacheKey, cachedPurchaseMotions.sessionCache]);
  return /*#__PURE__*/_jsx(FeatureTableDataCacheContext.Provider, {
    value: {
      cachedBuyingMotionProps,
      cachedCurrency,
      cachedFeatureCategories,
      cachedOwnedProductsWithinSkuCategory,
      cachedProducts,
      cachedPrices,
      cachedPurchaseMotions,
      isPreSeatsPortal
    },
    children: children
  });
};

// The following function is used to reset the cache for testing purposes ONLY
export const resetFeatureTableCaches = () => {
  resetCachedProductInventory();
  MODULE_FETCH_INITIALIZED_CACHE = {
    featureCategories: {},
    purchaseMotions: {},
    products: {},
    purchaseOptions: {},
    ownedProductsWithinSkuCategory: {}
  };
  MODULE_IS_PRE_SEATS_PORTAL_CACHE = undefined;
  MODULE_BUYING_MOTION_PROPS_CACHE = {
    sessionCache: {}
  };
  MODULE_CURRENCY_CACHE = undefined;
  MODULE_FEATURE_CATEGORIES_CACHE = {
    sessionCache: {}
  };
  MODULE_OWNED_PRODUCTS_CACHE = {
    allOwnedProducts: undefined,
    sessionCache: {}
  };
  MODULE_PRICES_CACHE = {
    sessionCache: {}
  };
  MODULE_PRODUCTS_CACHE = {
    sessionCache: {}
  };
  MODULE_PURCHASE_MOTIONS_CACHE = {
    sessionCache: {}
  };
};